import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch, batch } from "react-redux";
import { Accordion, Card, Button, Dropdown, Form, Alert, Modal } from "react-bootstrap";
import LeMoulinApi from "app/universal/utils/LeMoulinApi";
import { REFUSAL_REASONS } from "helper/refusalReasons";
import moment from "moment";
import { centimesToCurrency, toFrenchType } from "helper/globalHelper";
import { multiOrdersStoreToApi } from "helper/transformDataHelper";
import OrderApiSchema from "schema/Order/OrderApiSchema";
import {
  saveGroupedOrder,
  removeOrder,
  moveOrder,
  setGroupedOrderStatus,
  duplicatePrestation,
  setCurrentOrderIndex
} from "state/orderEdition/actions";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { updateModal } from "state/adminData/actions";
import swal from 'sweetalert';
import { SHOW_LEGACY_LMVP, openLmvp } from "helper/lmvpLinks";
import { indexAfterRemoval } from "helper/prestationIndex";
import { faTrash, faSave, faUser, faCopy, faFileAlt, faGripVertical } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { useLocation, useHistory } from 'react-router-dom';
import SaveGabaritModal from "app/orderTool/components/Gabarit/SaveGabaritModal";

const OrderComponent = ({ orderIndex }) => {
  const dispatch = useDispatch();

  const order = useSelector(
    (state) => state.orderEdition.orders[orderIndex] || {}
  );
  const groupedOrderId = useSelector(
    (state) => state.orderEdition.groupedOrderId
  );
  const currentOrderIndex = useSelector(
    (state) => state.orderEdition.currentOrderIndex
  );
  const ordersCount = useSelector(
    (state) => state.orderEdition.orders.length
  );

  // En mode brique, on calcule le total live depuis briqueDevis pour que
  // l'affichage se mette à jour en temps réel sans attendre un save.
  // unit_price / price_per_person sont en centimes (convention CurrencyComponent).
  const liveBriqueTotalHT = order.briqMode
    ? (order.briqueDevis || [])
        .flatMap(block => block.items || [])
        .filter(item => item.type !== 'free_text' && item.type !== 'text')
        .reduce((sum, item) => {
          if (item.type === 'forfait') {
            return sum + (item.price_per_person || 0) * (item.nb_persons || 0);
          }
          return sum + (item.unit_price || 0) * (item.quantity || 1);
        }, 0)
      + (order.deliveryCost || 0)
    : null;

  const totalHT = order.briqMode
    ? liveBriqueTotalHT
    : (order?.priceInfo?.totalHT || 0);

  // Mode classique : lignes depuis order.items. Mode brique : rendu par blocs (voir plus bas).
  // Exclut les Articles "Repas staff" du récap visible : ils sont déjà
  // listés dans la section dédiée "🍽️ Repas équipe" du ServiceInfoComponent
  // et ne doivent pas polluer "AU DÉTAIL" / "MATÉRIEL & SERVICES".
  const displayItems = order.briqMode
    ? []
    : (order.items || []).filter((item) => item.category !== 'Repas staff');

  const sortedBriqueBlocks = useMemo(() => {
    if (!order.briqMode) return [];
    const blocks = [...(order.briqueDevis || [])];
    blocks.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
    return blocks;
  }, [order.briqMode, order.briqueDevis]);

  const briqueItemLabel = (item) =>
    item?.name_override ||
    item?.renammed ||
    item?.effective_name ||
    item?.name ||
    "";

  const sortedBriqueItems = (items) =>
    [...(items || [])].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

  const location = useLocation();
  const history = useHistory();

  const handleToggleClick = (orderIndex) => {
    // Ajoute l'order index dans l'url également
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('order_index', orderIndex);
    const newUrl = `${location.pathname}?${queryParams.toString()}`;
    history.push(newUrl);
};

  const handleRemoveOrder = (orderIndex) => {
    swal({
      title: "Etes-vous sûre de vouloir supprimer cette prestation ?",
      text: "Merci de sauvegarder votre commande pour que la suppression prennent effet",
      icon: "warning",
      buttons: ["Ne pas supprimer", "Je suis sûre"],
    })
    .then((confirm) => {
      if(confirm){
        // Calculé avant le dispatch, avec le state d'avant la suppression
        const newIndex = indexAfterRemoval(currentOrderIndex, orderIndex, ordersCount - 1);

        // Suppression + URL ?order_index= en un seul rendu (React 16 ne regroupe pas
        // les mises à jour hors événement) : sinon le useEffect de MultiOrders relit
        // l'ancienne URL entre les deux et ouvre une autre prestation au passage.
        batch(() => {
          dispatch(removeOrder(orderIndex));
          if (newIndex !== null) {
            const queryParams = new URLSearchParams(location.search);
            queryParams.set('order_index', newIndex);
            history.push(`${location.pathname}?${queryParams.toString()}`);
          }
        });
      }
    });
    
  };

  const handleDuplicate = (orderIndex) => {
    swal({
      title: "Etes-vous sûre de vouloir dupliquer cette prestation ?",
      text: "Merci de sauvegarder votre commande après la duplication pour que cela prenne effet",
      icon: "warning",
      buttons: ["Ne pas dupliquer", "Je suis sûre"],
    })
    .then((confirm) => {
      if(confirm){
        const newIndex = orderIndex + 1;
        dispatch(duplicatePrestation(orderIndex));
        dispatch(setCurrentOrderIndex(newIndex));

        const queryParams = new URLSearchParams(location.search);
        queryParams.set('order_index', newIndex);
        const newUrl = `${location.pathname}?${queryParams.toString()}`;
        history.push(newUrl);
      }
    });
    
  };

  return (
    <>
      <Card key={orderIndex}>
        <Card.Header style={{ position: 'relative' }}>
          <Tippy content="Glissez-déposez pour réordonner les prestations (même date)">
            <span
              style={{
                position: 'absolute',
                top: '8px',
                right: '10px',
                color: '#adb5bd',
                cursor: 'grab',
              }}
            >
              <FontAwesomeIcon icon={faGripVertical} />
            </span>
          </Tippy>
          <Accordion.Toggle
            as={Button}
            variant="link"
            eventKey={orderIndex.toString()}
            onClick={() => handleToggleClick(orderIndex)}
            style={{ textAlign: 'left', color: 'black' }}
          >
            
           {toFrenchType(order.prestationType, true)} du {order.deliveryInfo.deliveryDate ? moment(order.deliveryInfo.deliveryDate).format("DD/MM/YY") : "A RENSEIGNER"} pour {order.deliveryInfo.person ? order.deliveryInfo.person : "A RENSEIGNER"} <FontAwesomeIcon icon={faUser} /><br/>
           <small>{order?.info?.orderNumber && order.info.orderNumber} - {centimesToCurrency(totalHT)} HT</small><br/>
           {order?.id ?
              <Dropdown style={{ display: 'inline-block', marginRight: '10px' }}>
                <Dropdown.Toggle
                  variant="outline-secondary"
                  size="sm"
                  id="dropdown-custom-components"
                >
                  Documents
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {order?.info?.document_urls && order.info.document_urls.length > 0 ? (
                    order.info.document_urls.map((document, index) => (
                      <Dropdown.Item
                        key={index * 10}
                        href={document.url}
                        target="_blank"
                      >
                        {document.name}
                      </Dropdown.Item>
                    ))
                  ) : (
                    <Dropdown.Item
                      eventKey="10"
                      href={order?.info?.generate_document_url}
                      target="_blank"
                    >
                      Générer le document manquant
                    </Dropdown.Item>
                  )}
                  {!order?.cancel_date && (
                    <>
                      <hr />
                      <Dropdown.Item
                        eventKey="1"
                        href={order?.info?.quotation_url}
                        target="_blank"
                      >
                        Fiche de production
                      </Dropdown.Item>
                      {SHOW_LEGACY_LMVP && (
                        <>
                          <Dropdown.Item
                            eventKey="2"
                            href={order?.info?.propose_url}
                            target="_blank"
                          >
                            LMVP (court)
                          </Dropdown.Item>
                          <Dropdown.Item
                            eventKey="3"
                            href={order?.info?.propose_long_url}
                            target="_blank"
                          >
                            LMVP (long)
                          </Dropdown.Item>
                        </>
                      )}
                      <Dropdown.Item
                        eventKey="lmvp-v2"
                        onSelect={() => openLmvp(order?.info?.propose_v2_url, groupedOrderId)}
                      >
                        LMVP 2026
                      </Dropdown.Item>
                      <Dropdown.Item eventKey="4" href={`/feuille-de-route/${order.id}`} target="_blank">
                        Feuille de route
                      </Dropdown.Item>
                      <Dropdown.Item eventKey="menu" href={`/menu-a-imprimer/${order.id}`} target="_blank">
                        Menu à imprimer
                      </Dropdown.Item>
                      
                    </>
                  )}
                </Dropdown.Menu>
              </Dropdown>
              : <Button
                  variant="outline-secondary"
                  size="sm"
                  style={{ marginRight: '10px' }}
                  disabled
                >
                  Non sauvegardé
                </Button>
            }
          </Accordion.Toggle>
        </Card.Header>
        <Accordion.Collapse eventKey={orderIndex.toString()}>
          <Card.Body>
            <Button
              onClick={(e) => handleRemoveOrder(orderIndex)}
              variant="outline-danger"
              size="sm"
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
            <Button
              onClick={(e) => handleDuplicate(orderIndex)}
              variant="outline-info"
              size="sm"
              style={{ marginLeft: '10px' }}
            >
              <FontAwesomeIcon icon={faCopy} />
            </Button>
            <hr />
            <ul style={{ listStyleType: 'none', paddingLeft: '0' }}>
            {order.format && <li style={{ color: "grey" }}><small>Format : {order.format}</small></li>}

              {order.briqMode ? (
                <>
                  {sortedBriqueBlocks.map((block, blockIdx) => {
                    const items = sortedBriqueItems(block.items);
                    const lines = [];
                    items.forEach((item, idx) => {
                      const keyBase = item._localId || `b-${blockIdx}-i-${idx}`;
                      if (item.type === 'text' || item.type === 'free_text') {
                        const t = (item.text || '').trim();
                        if (t) {
                          lines.push(
                            <li key={keyBase} style={{ color: "grey" }}>
                              <small style={{ fontStyle: "italic" }}>{t}</small>
                            </li>
                          );
                        }
                        return;
                      }
                      if (item.type === 'forfait') {
                        const q = item.nb_persons ?? item.quantity ?? 1;
                        const label = briqueItemLabel(item);
                        if (!label) return;
                        lines.push(
                          <li key={keyBase} style={{ color: "grey" }}>
                            <small>
                              <span style={{ color: "green" }}>{q}</span> x {label}
                            </small>
                          </li>
                        );
                        return;
                      }
                      if (
                        item.type === 'group' ||
                        item.type === 'product' ||
                        item.type === 'part'
                      ) {
                        const q = item.quantity ?? 1;
                        const label = briqueItemLabel(item);
                        if (!label) return;
                        lines.push(
                          <li key={keyBase} style={{ color: "grey" }}>
                            <small>
                              <span style={{ color: "green" }}>{q}</span> x {label}
                            </small>
                          </li>
                        );
                      }
                    });
                    if (lines.length === 0) return null;
                    return (
                      <React.Fragment key={block.id ?? block._devisId ?? `blk-${blockIdx}`}>
                        {(block.name || '').trim() ? (
                          <div
                            style={{
                              color: "grey",
                              fontWeight: "bold",
                              marginTop: blockIdx === 0 ? "6px" : "10px",
                            }}
                          >
                            <small>
                              <b><u>{String(block.name).toUpperCase()}</u></b>
                            </small>
                          </div>
                        ) : null}
                        {lines}
                      </React.Fragment>
                    );
                  })}
                  <li style={{ color: "grey", marginTop: "10px" }}>
                    <small>Livraison : {centimesToCurrency(order.deliveryCost)}</small>
                  </li>
                </>
              ) : (
                <>
                  {displayItems.filter(item => item.type === 'productPackage').length > 0 && (
                    <>
                      <div style={{ color: "grey", fontWeight: "bold", marginTop: "10px" }}><small><b><u>FORFAITS</u></b></small></div>
                      {displayItems
                        .filter(item => item.type === 'productPackage')
                        .map((item, index) => {
                          return (
                            <li key={`package-${index}`} style={item.has_archived_reference ? { color: "#dc3545", fontWeight: 600 } : { color: "grey" }} title={item.has_archived_reference ? "⚠️ Le forfait lié a été archivé — supprimez ou remplacez avant sauvegarde." : undefined}>
                              <small><span style={{ color: "green" }}>{item.quantity}</span> x {item?.renammed || item.name}{item.has_archived_reference ? " ⚠️" : ""}</small>
                            </li>
                          );
                        })}
                    </>
                  )}

                  {displayItems.filter(item => item.type === 'productGroup').length > 0 && (
                    <>
                      <div style={{ color: "grey", fontWeight: "bold", marginTop: "10px" }}><small><b><u>GROUPES DE PRODUITS</u></b></small></div>
                      {displayItems
                        .filter(item => item.type === 'productGroup')
                        .map((item, index) => {
                          return (
                            <li key={`group-${index}`} style={item.has_archived_reference ? { color: "#dc3545", fontWeight: 600 } : { color: "grey" }} title={item.has_archived_reference ? "⚠️ Le groupe de produits lié a été archivé — supprimez ou remplacez avant sauvegarde." : undefined}>
                              <small><span style={{ color: "green" }}>{item.quantity}</span> x {item?.renammed || item.name}{item.has_archived_reference ? " ⚠️" : ""}</small>
                            </li>
                          );
                        })}
                    </>
                  )}

                  {displayItems.filter(item => item.type === 'formula').length > 0 && (
                    <>
                      <div style={{ color: "grey", fontWeight: "bold", marginTop: "10px" }}><small><b><u>FORMULES</u></b></small></div>
                      {displayItems
                        .filter(item => item.type === 'formula')
                        .map((item, index) => {
                          return (
                            <li key={`formula-${index}`} style={{ color: "grey" }}>
                              <small><span style={{ color: "green" }}>{item.quantity}</span> x {item?.renammed || item.name}</small>
                            </li>
                          );
                        })}
                    </>
                  )}

                  {displayItems.filter(item => item.type === 'product' && item.classification_name !== 'Matériels et services').length > 0 && (
                    <>
                      <div style={{ color: "grey", fontWeight: "bold", marginTop: "10px" }}><small><b><u>AU DÉTAIL</u></b></small></div>
                      {displayItems
                        .filter(item => item.type === 'product' && item.classification_name !== 'Matériels et services')
                        .map((item, index) => {
                          return (
                            <li key={`product-${index}`} style={item.has_archived_reference ? { color: "#dc3545", fontWeight: 600 } : { color: "grey" }} title={item.has_archived_reference ? "⚠️ Le produit lié a été archivé — supprimez ou remplacez avant sauvegarde." : undefined}>
                              <small><span style={{ color: "green" }}>{item.quantity}</span> x {item?.caterer_name && order.prestationType === 'caterer' ? item.caterer_name : (item?.renammed || item.name)}{item.has_archived_reference ? " ⚠️" : ""}</small>
                            </li>
                          );
                        })}
                    </>
                  )}

                  {displayItems.filter(item => item.type === 'product' && item.classification_name === 'Matériels et services').length > 0 && (
                    <>
                      <div style={{ color: "grey", fontWeight: "bold", marginTop: "10px" }}><small><b><u>MATÉRIEL & SERVICES</u></b></small></div>
                      {displayItems
                        .filter(item => item.type === 'product' && item.classification_name === 'Matériels et services')
                        .map((item, index) => {
                          return (
                            <li key={`material-${index}`} style={item.has_archived_reference ? { color: "#dc3545", fontWeight: 600 } : { color: "grey" }} title={item.has_archived_reference ? "⚠️ Le produit lié a été archivé — supprimez ou remplacez avant sauvegarde." : undefined}>
                              <small><span style={{ color: "green" }}>{item.quantity}</span> x {item?.renammed || item.name}{item.has_archived_reference ? " ⚠️" : ""}</small>
                            </li>
                          );
                        })}
                    </>
                  )}

                  <li style={{ color: "grey", marginTop: "10px" }}><small>Livraison : {centimesToCurrency(order.deliveryCost)}</small></li>
                </>
              )}
            </ul>
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </>
  );
};

const ListOrdersComponent = () => {
  const routerHistory = useHistory();
  const routerLocation = useLocation();
  const orders = useSelector((state) => state.orderEdition.orders);
  const sortedOrders = useMemo(() => {
    return orders
      .map((order, index) => ({ order, index }))
      .sort((a, b) => {
        const dateA = a.order?.deliveryInfo?.deliveryDate;
        const dateB = b.order?.deliveryInfo?.deliveryDate;

        if (!dateA && !dateB) {
          return a.index - b.index;
        }
        if (!dateA) {
          return 1;
        }
        if (!dateB) {
          return -1;
        }

        // À date égale, respecte l'ordre du tableau redux (réordonnable par drag & drop)
        return new Date(dateA) - new Date(dateB) || a.index - b.index;
      });
  }, [orders]);
  // Articles dont au moins une FK (product / productPart / group / package) pointe
  // vers une entité offre archivée (cf. `archivedArticles` posé par transformDataHelper
  // depuis l'API field `has_archived_reference`). Bloque la sauvegarde tant qu'il en
  // reste — pour éviter qu'un devis avec un produit archivé soit poussé à VosFactures
  // avec une ligne fantôme (cf. ArchivedArticlesGuard côté backend).
  const allArchivedArticles = useMemo(() => {
    return (orders || []).flatMap((order, orderIdx) =>
      (order.archivedArticles || []).map((a) => ({ ...a, orderIndex: orderIdx }))
    );
  }, [orders]);
  const hasArchivedRefs = allArchivedArticles.length > 0;

  const groupedOrderUpdatedAt = useSelector(
    (state) => state.orderEdition.groupedOrderUpdatedAt
  );
  const currentOrderIndex = useSelector(
    (state) => state.orderEdition.currentOrderIndex
  );
  const groupedOrderStatus = useSelector(
    (state) => state.orderEdition.groupedOrderStatus
  );
  // Totaux de la commande groupée calculés par l'API à la dernière sauvegarde.
  // Le TTC y est calculé par taux de TVA sur toute la facture : il peut
  // différer d'un centime de la somme des TTC des prestations.
  const groupedOrderTotalHT = useSelector(
    (state) => state.orderEdition.groupedOrderTotalHT
  );
  const groupedOrderTotalTTC = useSelector(
    (state) => state.orderEdition.groupedOrderTotalTTC
  );

  const dispatch = useDispatch();

  const [totalHT, setTotalHT] = useState(0);
  const [totalHTAvantRemise, setTotalHTAvantRemise] = useState(0);
  const [totalTTC, setTotalTTC] = useState(0);
  const [totalTTCAvantRemise, setTotalTTCAvantRemise] = useState(0);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [status, setStatus] = useState(groupedOrderStatus || "");
  const [showGabaritModal, setShowGabaritModal] = useState(false);
  const [showRefusalModal, setShowRefusalModal] = useState(false);
  const [refusalReason, setRefusalReason] = useState('');
  const [refusalReasonDetail, setRefusalReasonDetail] = useState('');

  const orderEdition = useSelector((state) => state.orderEdition);
  const adminData = useSelector((state) => state.adminData);
  const tenderCatalogData = useSelector((state) => state.tender.tenderCatalog?.data || null);
  const rotationSlotsData = useSelector((state) => state.rotationSlots?.data || []);

  const groupedOrderId = useSelector(
    (state) => state.orderEdition.groupedOrderId
  );

  useEffect(() => {
    // Pour les prestations en mode brique, on calcule le total live depuis briqueDevis
    // plutôt que depuis priceInfo.totalHT (qui n'est mis à jour qu'après un save API).
    const getOrderTotalHTAvantRemise = (order) => {
      if (order.briqMode) {
        return (order.briqueDevis || [])
          .flatMap(block => block.items || [])
          .filter(item => item.type !== 'free_text' && item.type !== 'text')
          .reduce((sum, item) => {
            if (item.type === 'forfait') {
              return sum + (item.price_per_person || 0) * (item.nb_persons || 0);
            }
            return sum + (item.unit_price || 0) * (item.quantity || 1);
          }, 0)
          + (order.deliveryCost || 0);
      }
      return order?.priceInfo?.totalHTWithoutReduction || order?.priceInfo?.totalHT || 0;
    };

    // Montant de la remise, mêmes règles que orderStateToTotal (helper de
    // sauvegarde) : direct en centimes, proportionnel en % de la base HT.
    const getOrderRemise = (order, baseHT) => {
      const reductions = Array.isArray(order?.reductions)
        ? order.reductions
        : (order?.reduction && (order.reduction.direct || order.reduction.proportional) ? [order.reduction] : []);
      return reductions.reduce((sum, r) => {
        if (r?.proportional) {
          return sum + (baseHT * Number(r.proportional)) / 100;
        }
        if (r?.direct) {
          return sum + (Number(r.direct) || 0);
        }
        return sum;
      }, 0);
    };

    const getOrderTotalHT = (order) => {
      const avant = getOrderTotalHTAvantRemise(order);
      if (order.briqMode) {
        return avant - getOrderRemise(order, avant);
      }
      return order?.priceInfo?.totalHT || 0;
    };

    const total = orders.reduce((acc, order) => acc + getOrderTotalHT(order), 0);
    const totalAvantRemise = orders.reduce((acc, order) => acc + getOrderTotalHTAvantRemise(order), 0);
    // TTC : seul le back sait ventiler la TVA par ligne, ces montants viennent
    // donc de la dernière sauvegarde (priceInfo) et non d'un calcul live.
    const ttcSumOfOrders = orders.reduce((acc, order) => acc + (order?.priceInfo?.totalTTC || 0), 0);
    // Le TTC de l'API (TVA par taux sur toute la facture) fait foi tant que le
    // HT affiché est celui de la dernière sauvegarde ; après une modification
    // non enregistrée, on retombe sur la somme des prestations.
    const savedTotalsMatch =
      Number.isFinite(groupedOrderTotalHT) &&
      Number.isFinite(groupedOrderTotalTTC) &&
      Math.round(total) === groupedOrderTotalHT;
    const ttc = savedTotalsMatch ? groupedOrderTotalTTC : ttcSumOfOrders;
    const ttcAvantRemise = orders.reduce(
      (acc, order) => acc + (order?.priceInfo?.totalTTCWithoutReduction || order?.priceInfo?.totalTTC || 0),
      0
    );

    // Convives comptés par DATE de livraison et non par prestation : plusieurs
    // prestations le même jour partagent les mêmes participants (max du jour).
    // Les prestations sans date comptent chacune individuellement.
    const participantsByDate = {};
    let undatedParticipants = 0;
    orders.forEach((order) => {
      const orderParticipants = parseInt(order?.deliveryInfo?.person, 10);
      if (!Number.isFinite(orderParticipants)) {
        return;
      }
      const date = order?.deliveryInfo?.deliveryDate;
      if (date) {
        participantsByDate[date] = Math.max(participantsByDate[date] || 0, orderParticipants);
      } else {
        undatedParticipants += orderParticipants;
      }
    });
    const participants =
      Object.values(participantsByDate).reduce((acc, val) => acc + val, 0) +
      undatedParticipants;

    setTotalHT(total);
    setTotalHTAvantRemise(totalAvantRemise);
    setTotalTTC(ttc);
    setTotalTTCAvantRemise(ttcAvantRemise);
    setTotalParticipants(participants);
  }, [orders, groupedOrderTotalHT, groupedOrderTotalTTC]);

  const pricePerPersonHT = totalParticipants > 0 ? Math.round(totalHT / totalParticipants) : 0;
  const hasRemise = Math.round(totalHTAvantRemise) !== Math.round(totalHT);

  const handleSave = async () => {
    try {
      const briqueCatalog = {
        products: adminData.products?.data || [],
        productGroups: adminData.productGroups?.data || [],
        productPackages: adminData.productPackages?.data || [],
        productParts: adminData.productParts?.data || [],
        tenderCatalog: tenderCatalogData,
        rotationSlots: rotationSlotsData,
      };
      const data = multiOrdersStoreToApi(orderEdition, briqueCatalog);
      await Promise.all(data.map(orderData => OrderApiSchema.validate(orderData)));
      
      // Dispatch l'action et attendre qu'elle soit terminée
      const result = await dispatch(saveGroupedOrder({
        orders: data,
        savInProgress: orderEdition.savInProgress === true || orderEdition.savInProgress === "1" || orderEdition.savInProgress === 1
      }, groupedOrderId)).then((res, a) => {
        if (res?.groupedOrder?.id) {
          const url = new URL(window.location);
          url.searchParams.set('order_id', res.groupedOrder.id);
          url.searchParams.set('order_type', 'v2');
          window.history.pushState({}, '', url);
          // Devis ouvert depuis le pipeline (ou le SAV) : on propose d'y revenir plutôt
          // que vers la liste des commandes (retour terrain Keep, 26/10/2026).
          const provenance = url.searchParams.get('from');
          const RETOUR_APRES_SAUVEGARDE = {
            pipeline: { libelle: "Retour au pipeline", chemin: "/pipeline-b2b" },
            sav: { libelle: "Retour au SAV", chemin: "/pipeline-sav" },
          };
          const retourApresSauvegarde = RETOUR_APRES_SAUVEGARDE[provenance] || null;
          swal({
            title: "Enregistrement fait",
            text: "Votre commande a bien été sauvegardé et le devis généré",
            icon: "success",
            buttons: ["Rester ici", retourApresSauvegarde ? retourApresSauvegarde.libelle : "Retour aux commandes"],
          })
          .then((confirm) => {
            if(confirm && retourApresSauvegarde){
              routerHistory.push(retourApresSauvegarde.chemin);
            } else if(confirm){
              routerHistory.push({
                pathname: `/`,
                search: window.location.search
              });
            }
          });
        }
        if(res.success === false){
          swal({
            title: "Oups",
            text: res.message,
            icon: "error",
          })
        }
        // 400 backend (cf. apiService.js : `data._apiError` préservé) — typiquement
        // `ArchivedArticlesGuard` qui refuse la sauvegarde si un Article pointe
        // vers un produit archivé. Affiche un swal détaillé pour aider l'utilisateur.
        if (res?.error === true && res?._apiError) {
          const moreInfo = res._apiError.more_info;
          if (moreInfo && moreInfo.reason === 'archived_article_references') {
            const list = (moreInfo.archived_articles || [])
              .slice(0, 15)
              .map((a) => `• ${a.name || `Article #${a.article_id}`} (${(a.archived_fields || []).join(', ') || '—'})`)
              .join('\n');
            const extra = (moreInfo.archived_articles || []).length > 15
              ? `\n… et ${(moreInfo.archived_articles || []).length - 15} autres.`
              : '';
            swal({
              title: "Sauvegarde refusée — produit(s) archivé(s)",
              text: `${res._apiError.message}\n\nArticles concernés :\n${list}${extra}`,
              icon: "error",
            });
          } else {
            swal({
              title: "Oups",
              text: res._apiError.message || "Une erreur est survenue lors de la sauvegarde.",
              icon: "error",
            });
          }
        }
      });

      // Récupérer le nouvel groupedOrderId depuis la réponse ou l'état mis à jour
      const newGroupedOrderId = result?.groupedOrder?.id || groupedOrderId;
  
      // Ajouter le nouvel groupedOrderId à l'URL
      if (newGroupedOrderId) {
        const url = new URL(window.location);
        url.searchParams.set('order_id', newGroupedOrderId);
        window.history.pushState({}, '', url);
      }
    }
    catch (error) {
      dispatch(updateModal({
        isOpen: true,
        title: "Erreur de validation",
        message: error.message,
      }));
    }
  };

  // Assumons que vous avez une liste de statuts
  const statusOptions = [
    {
      option: "101",
      traduction: "En attente",
    },
    {
      option: "102",
      traduction: "Validé",
    },
    {
      option: "401",
      traduction: "Refusé par le client",
    },
  ];

  const statusCheckOptions = [
    {
      option: "103",
      traduction: "A vérifier avant facturation",
    },
    {
      option: "201",
      traduction: "A payer",
    },
  ];

  const handleStatusChange = (e) => {
    const newStatus = e.target.value;
    // Passage en refusé → on demande d'abord le motif via une popup.
    if (['400', '401', '402', '411', '412'].includes(newStatus)) {
      setRefusalReason('');
      setRefusalReasonDetail('');
      setShowRefusalModal(true);
      return;
    }
    setStatus(newStatus);
    dispatch(setGroupedOrderStatus(newStatus));
  };

  // Validation de la popup motif : applique le statut refusé + persiste le motif.
  const confirmRefusal = () => {
    if (!refusalReason) return;
    setStatus('401');
    dispatch(setGroupedOrderStatus('401'));
    setShowRefusalModal(false);
    // Le devis existant : on enregistre statut + motif immédiatement (réutilise
    // l'endpoint grouped status, qui valide et propage le motif aux commandes).
    if (groupedOrderId) {
      LeMoulinApi.request.grouped.status(groupedOrderId, {
        status: '401',
        refusal_reason: refusalReason,
        refusal_reason_detail: refusalReason === 'autre' ? (refusalReasonDetail || null) : null,
      });
    }
  };
  // Drag & drop des prestations : on traduit les positions visuelles (liste
  // triée par date) en index du tableau redux. Le tri par date reste prioritaire :
  // le réordonnancement manuel joue à l'intérieur d'une même date (et est
  // persisté via le champ `sort` envoyé à la sauvegarde).
  const handleOrderDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }
    const fromIndex = sortedOrders[result.source.index]?.index;
    const toIndex = sortedOrders[result.destination.index]?.index;
    if (fromIndex === undefined || toIndex === undefined || fromIndex === toIndex) {
      return;
    }
    dispatch(moveOrder(fromIndex, toIndex));

    // Fait suivre l'URL ?order_index= à la prestation ouverte (même remap que le
    // reducer MOVE_ORDER) : sans ça, le useEffect de MultiOrders ré-impose l'ancien
    // index depuis l'URL et le panneau central bascule sur une autre prestation.
    let newCurrentIndex = currentOrderIndex;
    if (currentOrderIndex === fromIndex) {
      newCurrentIndex = toIndex;
    } else if (currentOrderIndex !== null && currentOrderIndex !== undefined) {
      if (fromIndex < currentOrderIndex && toIndex >= currentOrderIndex) {
        newCurrentIndex = currentOrderIndex - 1;
      } else if (fromIndex > currentOrderIndex && toIndex <= currentOrderIndex) {
        newCurrentIndex = currentOrderIndex + 1;
      }
    }
    if (newCurrentIndex !== null && newCurrentIndex !== undefined) {
      const queryParams = new URLSearchParams(routerLocation.search);
      if (parseInt(queryParams.get('order_index'), 10) !== newCurrentIndex) {
        queryParams.set('order_index', newCurrentIndex);
        routerHistory.push(`${routerLocation.pathname}?${queryParams.toString()}`);
      }
    }
  };

  return (

      <>
        <DragDropContext onDragEnd={handleOrderDragEnd}>
          <Droppable droppableId="prestations-list">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                <Accordion activeKey={currentOrderIndex ? currentOrderIndex.toString() : "0"}>
                  {sortedOrders.map(({ index }, position) => (
                    <Draggable
                      key={`prestation-${index}`}
                      draggableId={`prestation-${index}`}
                      index={position}
                    >
                      {(dragProvided, dragSnapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          style={{
                            ...(dragProvided.draggableProps.style || {}),
                            cursor: dragSnapshot.isDragging ? 'grabbing' : 'grab',
                          }}
                        >
                          <OrderComponent orderIndex={index} />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </Accordion>
              </div>
            )}
          </Droppable>
        </DragDropContext>
        
        <Alert variant="secondary" className="mt-2">
          
          Total HT : {centimesToCurrency(totalHT)}
          <br />
          {hasRemise && (
            <>
              <span className="text-muted">
                Avant remise : {centimesToCurrency(totalHTAvantRemise)} HT
                {totalTTCAvantRemise > 0 && <> / {centimesToCurrency(totalTTCAvantRemise)} TTC</>}
              </span>
              <br />
              <span className="text-muted">
                Après remise : {centimesToCurrency(totalHT)} HT
                {totalTTC > 0 && <> / {centimesToCurrency(totalTTC)} TTC</>}
              </span>
              <br />
            </>
          )}
          {!hasRemise && totalTTC > 0 && (
            <>
              Total TTC : {centimesToCurrency(totalTTC)}
              <br />
            </>
          )}
          Total participants : {totalParticipants}
          <br />
          Prix / participant : {centimesToCurrency(pricePerPersonHT)} HT
          {totalTTC > 0 && (
            <>
              <br />
              <small className="text-muted">
                TTC : montants de la dernière sauvegarde (la ventilation TVA est calculée côté serveur).
              </small>
            </>
          )}

        </Alert>

        <div className="d-grid gap-2">
          <Form.Group controlId="statusSelect">
            <Form.Label>Statut*</Form.Label>
            <Form.Control
              as="select"
              value={status}
              onChange={handleStatusChange}
            >
              <option value="" disabled>Sélectionnez un statut</option>
              {status === "103" || status === "201" ?
                statusCheckOptions.map((sta, index) => (
                  <option key={index} value={sta.option}>
                    {sta.traduction}
                  </option>
                ))
                :
                statusOptions.map((sta, index) => (
                  <option key={index} value={sta.option}>
                    {sta.traduction}
                  </option>
                ))
              }
            </Form.Control>
          </Form.Group>
          {hasArchivedRefs && (
            <Alert variant="danger" className="mb-2">
              <strong>⚠️ Sauvegarde bloquée — {allArchivedArticles.length} article{allArchivedArticles.length > 1 ? "s" : ""} pointe{allArchivedArticles.length > 1 ? "nt" : ""} vers un produit archivé.</strong>
              <div style={{ marginTop: "6px", fontSize: "0.85em" }}>
                Le produit lié n'est plus disponible au catalogue. Supprimez ou remplacez ces lignes (surlignées en rouge ci-dessus) avant de pouvoir sauvegarder.
                <ul style={{ marginTop: "6px", marginBottom: 0, paddingLeft: "1.2em" }}>
                  {allArchivedArticles.slice(0, 10).map((a) => (
                    <li key={`${a.orderIndex}-${a.id}`}>
                      <small>{a.name || `Article #${a.id}`} <em>(champs archivés : {(a.fields || []).join(", ") || "—"})</em></small>
                    </li>
                  ))}
                  {allArchivedArticles.length > 10 && (
                    <li><small><em>… et {allArchivedArticles.length - 10} autre{allArchivedArticles.length - 10 > 1 ? "s" : ""}</em></small></li>
                  )}
                </ul>
              </div>
            </Alert>
          )}
          <Button
            onClick={handleSave}
            variant={hasArchivedRefs ? "secondary" : "success"}
            size="xl"
            disabled={hasArchivedRefs}
            title={hasArchivedRefs ? "Sauvegarde bloquée : retirez ou remplacez les articles dont le produit a été archivé." : undefined}
            style={{ marginRight: "5px", width: "100%" }}
          >
            <FontAwesomeIcon icon={faSave} /> Sauvegarder<br/>
            <small>{groupedOrderUpdatedAt && `Sauvegardé le : ${moment(groupedOrderUpdatedAt).format('DD/MM/YY à HH:mm')}`}</small>
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowGabaritModal(true)}
            style={{ marginTop: "8px", width: "100%" }}
          >
            <FontAwesomeIcon icon={faFileAlt} style={{ marginRight: "6px" }} />
            Enregistrer en gabarit
          </Button>
          <SaveGabaritModal
            show={showGabaritModal}
            onHide={() => setShowGabaritModal(false)}
          />
        </div>

        <Modal show={showRefusalModal} onHide={() => setShowRefusalModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Motif du refus</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Pour quelle raison ce devis est-il refusé ?</p>
            <Form>
              {REFUSAL_REASONS.map((r) => (
                <Form.Check
                  key={r.key}
                  type="radio"
                  name="listRefusalReason"
                  id={`list-refusal-${r.key}`}
                  label={r.label}
                  checked={refusalReason === r.key}
                  onChange={() => setRefusalReason(r.key)}
                />
              ))}
              {refusalReason === 'autre' && (
                <Form.Group className="mt-2">
                  <Form.Label>Précisez la raison</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={refusalReasonDetail}
                    onChange={(e) => setRefusalReasonDetail(e.target.value)}
                    placeholder="Raison du refus..."
                  />
                </Form.Group>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowRefusalModal(false)}>
              Annuler
            </Button>
            <Button
              variant="danger"
              disabled={!refusalReason || (refusalReason === 'autre' && !refusalReasonDetail.trim())}
              onClick={confirmRefusal}
            >
              Confirmer le refus
            </Button>
          </Modal.Footer>
        </Modal>

      </>
    
  );
};

export default ListOrdersComponent;
